<template>
  <div :class="['goal-list-progress-chart']">
    <no-data v-if="chartData.length === 0" />
    <base-chart
      v-else
      :options="chartOptions"
    />
  </div>
</template>

<script>

import BaseChart from '@/components/dashboard/BaseChart.vue';
import NoData from '@/components/dashboard/NoData.vue';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import { AREA, DASH, DATETIME, DOT, LINE } from '@/lib/constants';
import { DateTime } from 'luxon';
import { getPointInLineBounded, linearRegression } from '@/lib/charts/line-chart';
import { getRoundedValue } from '@/lib/charts/format';
import { rgbaToHex } from 'shared/lib/color';

export default {
  name: 'GoalListProgressChart',
  props: {
    startDate: {
      type: Date,
      required: true,
    },
    endDate: {
      type: Date,
      required: true,
    },
    chartData: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { goalSettings } = useGoalSettings();
    return { goalSettings };
  },
  components: { BaseChart, NoData },
  computed: {
    progressData() {
      const first = {
        ...this.baseLine[0],
        expectedValue: this.baseLine[0].y,
      };
      const data = this.chartData
        .filter((e) => e.x >= Number(this.startDate) && e.x <= Number(this.endDate))
        .map((e) => ({
          x: e.x,
          y: e.y,
          expectedValue: this.expectedValue(e.x),
        }))
        .sort((a, b) => (a.x <= b.x ? -1 : 1));

      if (data.length === 0) {
        return [first];
      }
      if (data[0].x > this.baseLine[0].x) {
        data.unshift(first);
      }
      return data;
    },
    lowestProgressData() {
      const minY = Math.min(...this.progressData.map((pd) => pd.y));
      return this.progressData.find((pd) => pd.y === minY);
    },
    highestProgressData() {
      const maxY = Math.max(...this.progressData.map((pd) => pd.y));
      return this.progressData.find((pd) => pd.y === maxY);
    },
    trendData() {
      if (this.progressData.length < 2) {
        return this.baseLine;
      }

      const { slope, intercept } = linearRegression(this.progressData);
      return [
        { x: Number(this.startDate), y: intercept + (slope * Number(this.startDate)) },
        { x: Number(this.endDate), y: intercept + (slope * Number(this.endDate)) },
      ];
    },
    baseLine() {
      return [
        { x: Number(this.startDate), y: 0 },
        { x: Number(this.endDate), y: this.goalSettings.yellowThreshold },
      ];
    },
    redArea() {
      return [
        { x: Number(this.startDate), y: 0 },
        { x: Number(this.endDate), y: this.goalSettings.redThreshold },
      ];
    },
    yellowArea() {
      return [
        { x: Number(this.startDate), y: 0 },
        { x: Number(this.endDate), y: this.goalSettings.yellowThreshold - this.goalSettings.redThreshold },
      ];
    },
    greenArea() {
      let maxY = this.highestProgressData.y;
      if (maxY < 100) {
        maxY = 100;
      }

      return [
        { x: Number(this.startDate), y: maxY },
        { x: Number(this.endDate), y: maxY - this.goalSettings.yellowThreshold },
      ];
    },
    chartOptions() {
      const t = this.$t;
      return {
        legend: {
          enabled: true,
          align: 'left',
          verticalAlign: 'top',
          x: 50,
          floating: true,
        },
        plotOptions: {
          area: {
            stacking: 'normal',
            lineWidth: 0,
          },
        },
        series: [
          {
            states: { inactive: { opacity: 1 } },
            type: AREA,
            data: this.greenArea,
            color: this.$colors.green.base,
            fillOpacity: 0.2,
            marker: { enabled: false },
            enableMouseTracking: false,
            showInLegend: false,
          },
          {
            states: { inactive: { opacity: 1 } },
            type: AREA,
            data: this.yellowArea,
            color: this.$colors.yellow.base,
            fillOpacity: 0.2,
            marker: { enabled: false },
            enableMouseTracking: false,
            showInLegend: false,
          },
          {
            states: { inactive: { opacity: 1 } },
            type: AREA,
            data: this.redArea,
            color: this.$colors.red.base,
            fillOpacity: 0.2,
            marker: { enabled: false },
            enableMouseTracking: false,
            showInLegend: false,
            threshold: this.lowestProgressData.y < 0 ? this.lowestProgressData.y : 0,
          },
          {
            states: { inactive: { opacity: 1 } },
            type: LINE,
            dashStyle: DASH,
            data: this.trendData,
            color: this.$colors.grey.lighten3,
            marker: { enabled: false },
            enableMouseTracking: false,
            name: this.$t('highcharts.trend'),
          },
          {
            type: LINE,
            data: this.progressData,
            clip: false,
            color: rgbaToHex(this.$colors.grey.darken2),
            marker: {
              enabled: true,
              fillColor: this.$colors.shades.white,
              lineWidth: 2,
              lineColor: null, // inherit from series
              symbol: 'circle',
            },
            connectNulls: true,
            showInLegend: false,
          },
        ],
        time: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        xAxis: {
          lineColor: rgbaToHex(this.$colors.grey.lighten3),
          lineWidth: 1,
          type: DATETIME,
          minTickInterval: 28 * 24 * 3600 * 1000,
          minRange: 28 * 24 * 3600 * 1000,
          min: Number(this.startDate),
          max: Number(this.endDate),
        },
        yAxis: {
          min: this.lowestProgressData.y < 0 ? this.lowestProgressData.y : 0,
          max: this.highestProgressData.y > 100 ? this.highestProgressData.y : 100,
          lineWidth: 0,
          gridLineColor: rgbaToHex(this.$colors.grey.lighten2),
          gridLineDashStyle: DOT,
          title: { text: '' },
          labels: {
            formatter() {
              return `${this.value} %`;
            },
          },
        },
        tooltip: {
          shared: true,
          headerFormat: '',
          pointFormatter() {
            return `
<span style="font-size:10px">${DateTime.fromMillis(this.x).toLocaleString(DateTime.DATETIME_MED)}</span></br><br/>
${t('highcharts.actual')}: <span style="font-weight: 700">${this.y} %</span> </br><br/>
${t('highcharts.expected')}: <span style="font-weight: 700">${this.expectedValue} %</span>`;
          },
        },
      };
    },
  },
  methods: {
    expectedValue(timestamp) {
      return getRoundedValue(getPointInLineBounded(this.baseLine, timestamp).y, 2);
    },
  },
};
</script>
